import { ROUTES } from '../lib/routes';
import { navigate } from '../router';
import { showModal, showToast } from '../components/toast';
import { el } from '../utils/dom';
import { submitEv123Lead } from '../api/submitEv123Lead';

function textField(label: string, type: string, placeholder: string): HTMLElement {
  const wrap = el('div', { className: 'ev123-field' });
  const input = el('input', { className: 'ev123-input', type, placeholder }) as HTMLInputElement;
  wrap.append(el('label', { className: 'ev123-label', text: label }), input);
  return wrap;
}

function buildDiffLeadMessage(parts: { facility: string; areas: string; body: string }): string {
  return [
    `시설명/회사명/단지명: ${parts.facility}`,
    `상담 희망 분야: ${parts.areas}`,
    `문의 내용: ${parts.body}`,
  ].join('\n');
}

function svgGlyph(innerPaths: string): HTMLElement {
  const wrap = el('span', { className: 'ev123-diff-icon-card__glyph' });
  wrap.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${innerPaths}</svg>`;
  return wrap;
}

function sectionDiffHero(): HTMLElement {
  const sec = el('section', { className: 'ev123-diff-hero' });
  const inner = el('div', { className: 'ev123-container ev123-diff-hero__grid' });
  const left = el('div', {});
  const title = el('h1', { className: 'ev123-diff-hero__title' });
  title.append(
    document.createTextNode('전기차충전기 설치,'),
    el('br', {}),
    document.createTextNode('EVOS TEAM이 기준부터 운영까지 정리해드립니다.')
  );
  left.append(
    el('p', {
      className: 'ev123-portal-eyebrow ev123-portal-eyebrow--on-dark',
      text: 'EVOS TEAM · 전기차충전기 설치 컨설팅',
    }),
    title,
    el('p', {
      className: 'ev123-diff-hero__lead',
      text: '법령·현장 조건·운영 방식을 함께 보고, 설치와 운영 방향을 단계적으로 정리해드립니다.',
    }),
    el('div', { className: 'ev123-diff-hero__actions' }, [
      (() => {
        const b = el('button', {
          type: 'button',
          className: 'ev123-btn ev123-btn--primary ev123-btn--pill',
        }) as HTMLButtonElement;
        b.textContent = 'EVOS TEAM 상담 신청';
        b.addEventListener('click', () => {
          document.getElementById('ev123-diff-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return b;
      })(),
      (() => {
        const b = el('button', {
          type: 'button',
          className: 'ev123-btn ev123-btn--secondary ev123-btn--pill',
        }) as HTMLButtonElement;
        b.textContent = '충전기 설치 계산하기';
        b.addEventListener('click', () => navigate(ROUTES.newInstall));
        return b;
      })(),
    ])
  );

  inner.append(left);
  sec.appendChild(inner);
  return sec;
}

function sectionHeadline(title: string, lead: string, kicker?: string): HTMLElement {
  const parts: HTMLElement[] = [];
  if (kicker?.trim()) {
    parts.push(el('p', { className: 'ev123-section__kicker', text: kicker }));
  }
  parts.push(
    el('h2', { className: 'ev123-section__title ev123-section__title--home', text: title }),
    el('p', { className: 'ev123-section__lead ev123-section__lead--home', text: lead })
  );
  return el('div', { className: 'ev123-section-head ev123-section-head--center' }, parts);
}

function sectionProcessStepper(): HTMLElement {
  const sec = el('section', { className: 'ev123-section ev123-section--relaxed ev123-section--band' });
  const inner = el('div', { className: 'ev123-container' });
  inner.append(
    sectionHeadline(
      'EVOS TEAM 상담은 이렇게 진행됩니다.',
      '건물 조건을 먼저 확인하고, 결정이 필요한 내용을 순서대로 정리합니다.'
    )
  );
  const steps: { title: string; desc: string }[] = [
    {
      title: '기본정보 확인',
      desc: '주소, 건물유형, 주차면 수, 기존 충전기 현황을 확인합니다.',
    },
    {
      title: '설치기준 검토',
      desc: '의무설치 수량, 설치 가능 위치, 보조사업 가능성을 확인합니다.',
    },
    {
      title: '운영방식 비교',
      desc: '자가설치, 위탁운영, 보조사업, 수익형 모델 중 적합한 방향을 비교합니다.',
    },
    {
      title: '자료 구성',
      desc: '입대의, 내부보고, 사업자 비교, 견적 검토 자료를 정리합니다.',
    },
    {
      title: '설치 방향 제안',
      desc: '건물에 맞는 설치·운영 방향을 정리해 제안합니다.',
    },
  ];
  const track = el('div', { className: 'ev123-diff-stepper' });
  let n = 1;
  for (const s of steps) {
    const card = el('article', { className: 'ev123-diff-step' });
    card.append(
      el('div', { className: 'ev123-diff-step__badge', text: String(n) }),
      el('h3', { className: 'ev123-diff-step__title', text: s.title }),
      el('p', { className: 'ev123-diff-step__desc', text: s.desc })
    );
    track.appendChild(card);
    n += 1;
  }
  inner.appendChild(track);
  sec.appendChild(inner);
  return sec;
}

function iconCard(title: string, desc: string, svgInner: string): HTMLElement {
  const c = el('article', { className: 'ev123-diff-icon-card' });
  c.append(
    svgGlyph(svgInner),
    el('h3', { className: 'ev123-diff-icon-card__title', text: title }),
    el('p', { className: 'ev123-diff-icon-card__desc', text: desc })
  );
  return c;
}

function sectionCoreIconCards(): HTMLElement {
  const sec = el('section', { className: 'ev123-section ev123-section--relaxed ev123-section--surface' });
  const inner = el('div', { className: 'ev123-container' });
  inner.append(
    sectionHeadline(
      'EVOS TEAM이 정리하는 핵심 기준',
      '법령·위치·운영·보고까지, 설치 검토에서 자주 필요한 기준을 네 가지로 나눠 안내합니다.'
    )
  );
  const grid = el('div', { className: 'ev123-diff-icon-card-grid' });
  grid.append(
    iconCard(
      '법령 검토',
      '의무설치 여부와 필요 수량, 지자체 기준을 실무 관점으로 정리합니다.',
      '<path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/><path d="M8 7h8"/><path d="M8 11h8"/><path d="M8 15h5"/>'
    ),
    iconCard(
      '위치 판단',
      '주차면 배치, 전기실·동선, 접근성 등 설치 위치를 함께 검토합니다.',
      '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>'
    ),
    iconCard(
      '운영방식 비교',
      '자가·위탁·보조사업·수익형 등 운영 방식을 건물 조건에 맞게 비교합니다.',
      '<path d="M12 3l9 4.5v9L12 21l-9-4.5v-9L12 3z"/><path d="M12 12l9-4.5"/><path d="M12 12v9"/><path d="M12 12L3 7.5"/>'
    ),
    iconCard(
      '보고자료 정리',
      '입대의·관리사무소·건물주 등 내부 설명에 쓰일 자료 형태를 정리합니다.',
      '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/>'
    )
  );
  inner.appendChild(grid);
  sec.appendChild(inner);
  return sec;
}

function sectionTrust(): HTMLElement {
  const sec = el('section', { className: 'ev123-section ev123-section--tight ev123-section--band' });
  const inner = el('div', { className: 'ev123-container' });
  inner.append(
    sectionHeadline(
      'EVOS TEAM은 현장과 데이터를 함께 봅니다.',
      '설치 경험과 관리사무소 네트워크, 운영 데이터를 함께 참고합니다.',
      '신뢰근거'
    )
  );
  const grid = el('div', { className: 'ev123-diff-trust-grid' });
  const items: [string, string][] = [
    ['벤처기업', '기술 기반 전기차충전 인프라 서비스 기업'],
    ['8개 시도회 업무제휴', '주택관리사협회 시도회 네트워크 기반'],
    ['약 1,000개소 설치 경험', '전기차충전기 설치 건물 경험'],
    ['약 18,000개 DB', '공동주택·관리사무소 데이터 기반'],
  ];
  for (const [t, d] of items) {
    const c = el('div', { className: 'ev123-diff-trust-card' });
    c.append(
      el('p', { className: 'ev123-diff-trust-card__value', text: t }),
      el('p', { className: 'ev123-diff-trust-card__label', text: d })
    );
    grid.appendChild(c);
  }
  inner.appendChild(grid);
  sec.appendChild(inner);
  return sec;
}

const INTEREST_OPTIONS: string[] = [
  '신규설치 검토',
  '추가설치 검토',
  '교체설치 검토',
  '이전설치 검토',
  '운영관리 상담',
  '수익형충전기 검토',
  '기타',
];

export function renderDifferentiatedServicesPage(root: HTMLElement): void {
  const wrap = el('div', { className: 'ev123-page-diff' });

  wrap.append(sectionDiffHero(), sectionProcessStepper(), sectionCoreIconCards(), sectionTrust());

  const container = el('div', { className: 'ev123-container ev123-stack ev123-diff-bottom' });

  const formCard = el('div', { className: 'ev123-card ev123-card--muted ev123-diff-form-card', id: 'ev123-diff-form' });
  formCard.append(
    el('h2', { className: 'ev123-card__title ev123-card__title--sub', text: 'EVOS TEAM 상담 신청' }),
    el('p', {
      className: 'ev123-card__desc ev123-card__desc--sub',
      text: '건물 조건과 고민 중인 내용을 남겨주시면 전기차충전기 설치 방향을 검토해드립니다.',
    }),
    el('p', {
      className: 'ev123-diff-form-tip',
      text: '필수 항목만 간단히 적어 주셔도 됩니다. 구체적인 조건은 담당자가 연락드릴 때 함께 확인합니다.',
    })
  );

  const name = textField('담당자 성함', 'text', '실명을 입력해 주세요');
  const phone = textField('연락처', 'tel', '010-0000-0000');
  const facility = textField('시설명 / 회사명 / 단지명', 'text', '예: OO아파트, OO센터');

  const emailWrap = el('div', { className: 'ev123-field' });
  const emailInput = el('input', {
    className: 'ev123-input',
    type: 'email',
    placeholder: '선택 입력',
    'aria-label': '이메일',
  }) as HTMLInputElement;
  emailWrap.append(el('label', { className: 'ev123-label', text: '이메일' }), emailInput);

  const gridTop = el('div', { className: 'ev123-diff-form-grid' });
  const colA = el('div', { className: 'ev123-diff-form-grid__col' });
  const colB = el('div', { className: 'ev123-diff-form-grid__col' });
  colA.append(name, phone);
  colB.append(facility, emailWrap);
  gridTop.append(colA, colB);

  const interestField = el('div', { className: 'ev123-field' });
  interestField.append(el('label', { className: 'ev123-label', text: '상담 희망 분야' }));
  const interestChecks: HTMLInputElement[] = [];
  const interestGroup = el('div', { className: 'ev123-check-group ev123-check-group--stack' });
  for (const label of INTEREST_OPTIONS) {
    const row = el('label', { className: 'ev123-label ev123-label--check' });
    const cb = el('input', { type: 'checkbox', value: label }) as HTMLInputElement;
    interestChecks.push(cb);
    row.append(cb, document.createTextNode(` ${label}`));
    interestGroup.appendChild(row);
  }
  interestField.appendChild(interestGroup);

  const bodyField = el('div', { className: 'ev123-field' });
  const bodyInput = el('textarea', {
    className: 'ev123-textarea',
    rows: '4',
    placeholder: '문의하실 내용을 구체적으로 적어 주세요.',
  }) as HTMLTextAreaElement;
  bodyField.append(el('label', { className: 'ev123-label', text: '문의 내용' }), bodyInput);

  const privacyWrap = el('div', { className: 'ev123-field' });
  const privacyRow = el('div', { className: 'ev123-check-group' });
  const privacyChk = el('input', {
    type: 'checkbox',
    'aria-describedby': 'ev123-diff-privacy-hint',
  }) as HTMLInputElement;
  const privacyLabel = el('label', { className: 'ev123-label ev123-label--check' });
  privacyLabel.append(
    privacyChk,
    document.createTextNode(' 상담 접수를 위해 개인정보 수집 및 이용에 동의합니다.')
  );
  privacyRow.appendChild(privacyLabel);
  privacyWrap.append(
    privacyRow,
    el('p', {
      id: 'ev123-diff-privacy-hint',
      className: 'ev123-field-hint',
      text: '상담 처리를 위해 연락처와 문의 내용을 보관합니다.',
    })
  );

  formCard.append(gridTop, interestField, bodyField, privacyWrap);

  const submit = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--primary ev123-btn--block ev123-btn--pill ev123-diff-form-submit',
  }) as HTMLButtonElement;
  submit.textContent = 'EVOS TEAM 상담 신청하기';

  submit.addEventListener('click', async () => {
    const nameIn = name.querySelector('input') as HTMLInputElement | null;
    const phoneIn = phone.querySelector('input') as HTMLInputElement | null;
    const facIn = facility.querySelector('input') as HTMLInputElement | null;

    if (!nameIn?.value?.trim()) {
      showToast('담당자 성함을 입력해 주세요.');
      return;
    }
    if (!phoneIn?.value?.trim()) {
      showToast('연락처를 입력해 주세요.');
      return;
    }
    if (!facIn?.value?.trim()) {
      showToast('시설명 / 회사명 / 단지명을 입력해 주세요.');
      return;
    }
    const areas = interestChecks.filter((c) => c.checked).map((c) => c.value);
    if (areas.length === 0) {
      showToast('상담 희망 분야를 한 가지 이상 선택해 주세요.');
      return;
    }
    if (!bodyInput.value.trim()) {
      showToast('문의 내용을 입력해 주세요.');
      return;
    }
    if (!privacyChk.checked) {
      showToast('개인정보 수집 및 이용에 동의해 주세요.');
      return;
    }

    const message = buildDiffLeadMessage({
      facility: facIn.value.trim(),
      areas: areas.join(', '),
      body: bodyInput.value.trim(),
    });

    submit.disabled = true;
    const prevLabel = submit.textContent;
    submit.textContent = '접수 중…';
    try {
      await submitEv123Lead({
        name: nameIn.value.trim(),
        phone: phoneIn.value.trim(),
        apartment_or_company_name: facIn.value.trim(),
        inquiry_type: 'differentiated',
        message,
        privacy_agreed: true,
        email: emailInput.value.trim() || undefined,
      });
      showModal({
        title: '접수 완료',
        body: '요청이 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.',
        confirmLabel: '확인',
        onClose: () => navigate(ROUTES.homePublic),
      });
    } catch (e) {
      showToast(e instanceof Error ? e.message : '접수에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      submit.disabled = false;
      submit.textContent = prevLabel ?? 'EVOS TEAM 상담 신청하기';
    }
  });

  formCard.appendChild(submit);

  const cta = el('div', { className: 'ev123-card ev123-card--cta ev123-card--cta-panel' });
  cta.append(
    el('h2', { className: 'ev123-card__title', text: '전기차충전기 설치, 혼자 판단하지 마세요' }),
    el('p', {
      className: 'ev123-card__desc',
      text: '법령, 설치 위치, 운영방식, 보고자료까지 EVOS TEAM이 건물 조건에 맞춰 정리해드립니다.',
    }),
    el('div', { className: 'ev123-hero__actions ev123-hero__actions--tight' }, [
      (() => {
        const b = el('button', {
          type: 'button',
          className: 'ev123-btn ev123-btn--primary ev123-btn--pill',
        }) as HTMLButtonElement;
        b.textContent = '상담 신청';
        b.addEventListener('click', () => {
          document.getElementById('ev123-diff-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return b;
      })(),
      (() => {
        const b = el('button', {
          type: 'button',
          className: 'ev123-btn ev123-btn--secondary ev123-btn--pill',
        }) as HTMLButtonElement;
        b.textContent = '충전기 설치 계산';
        b.addEventListener('click', () => navigate(ROUTES.newInstall));
        return b;
      })(),
    ])
  );

  container.append(formCard, cta);
  wrap.appendChild(container);
  root.appendChild(wrap);
}
